/**
 * 로그 description: "요약 텍스트 | details={...json}" 형식 파싱
 * 슬롯 수정 시 메모는 서버가 memo / note / details.memo 등으로 줄 수 있어 통합합니다.
 */

export type DetailsChangedMap = Record<string, { before?: unknown; after?: unknown }>;

const DETAILS_MARKER = '| details=';

const MEMO_FIELD_ALIASES = ['note', 'notes', 'memo_text', 'Memo'] as const;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** details.changed 및 별도 메모 필드를 한 번에 훑어 memo 키로 합침 */
function normalizeMemoInChanged(changed: DetailsChangedMap, topLevelMemo?: unknown): DetailsChangedMap {
  const next: DetailsChangedMap = { ...changed };

  if (isPlainObject(topLevelMemo) && ('before' in topLevelMemo || 'after' in topLevelMemo)) {
    const m = topLevelMemo as { before?: unknown; after?: unknown };
    next.memo = {
      before: m.before ?? next.memo?.before,
      after: m.after ?? next.memo?.after,
    };
  }

  for (const alias of MEMO_FIELD_ALIASES) {
    const block = next[alias];
    if (!block) continue;
    if (!next.memo) {
      next.memo = { ...block };
    } else {
      next.memo = {
        before: next.memo.before ?? block.before,
        after: next.memo.after ?? block.after,
      };
    }
    delete next[alias];
  }

  return next;
}

export interface ParsedLogDetails {
  summary: string;
  changed: DetailsChangedMap;
}

/**
 * description 전체 문자열에서 요약과 changed 맵 추출 (메모 필드 정규화 포함)
 */
export function parseLogDetails(description: string | null | undefined): ParsedLogDetails | null {
  if (description == null || String(description).trim() === '') return null;

  const text = String(description);
  const idx = text.indexOf(DETAILS_MARKER);
  const summary = (idx === -1 ? text : text.slice(0, idx)).trim();

  if (idx === -1) {
    return { summary, changed: {} };
  }

  const jsonPart = text.slice(idx + DETAILS_MARKER.length).trim();
  let details: unknown;
  try {
    details = JSON.parse(jsonPart);
  } catch {
    return { summary, changed: {} };
  }

  if (!isPlainObject(details)) {
    return { summary, changed: {} };
  }

  const rawChanged = (details.changed ?? {}) as DetailsChangedMap;
  const topMemo = details.memo;
  const changed = normalizeMemoInChanged(rawChanged, topMemo);

  return { summary, changed };
}

/** 변경 필드 키만 추출 (엑셀 동적 컬럼 등) */
export function getChangedKeysFromDescription(description: string | null | undefined): string[] {
  const parsed = parseLogDetails(description);
  if (!parsed) return [];
  return Object.keys(parsed.changed);
}
